import { useState } from 'react';
import type { BankAccount } from '@stfors/shared-types';
import { can } from '../../auth/store';
import { ApiError } from '../../api/client';
import { useBankAccounts, useDeleteBankAccount } from '../../api/queries/bank-accounts';
import { BankAccountFormModal } from '../../components/BankAccountFormModal';
import './BankAccountsTab.css';

const TYPE_LABEL: Record<string, string> = {
  checking: 'Checking',
  savings: 'Savings',
  other: 'Other',
};

export function BankAccountsTab(): JSX.Element {
  const { data, isPending, error } = useBankAccounts();
  const del = useDeleteBankAccount();
  const canWrite = can('bank_account.write');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (a: BankAccount) => { setEditing(a); setModalOpen(true); };

  const onDelete = async (a: BankAccount) => {
    setRowError(null);
    if (!confirm(`Remove "${a.label}"? Past payments keep their record; it just leaves the picker.`)) return;
    try {
      await del.mutateAsync(a.id);
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : `Could not remove "${a.label}".`);
    }
  };

  const accounts = data?.bank_accounts ?? [];

  return (
    <div className="set-tab-body">
      <div className="ba-head">
        <p className="set-description">
          The firm's own deposit accounts. A recorded invoice payment can name
          which account received it. Only the last 4 digits are stored.
        </p>
        {canWrite && (
          <button type="button" className="btn-cyan-primary" onClick={openNew}>
            Add account
          </button>
        )}
      </div>

      {rowError && <p className="set-error" role="alert">{rowError}</p>}

      {isPending ? (
        <p className="set-status">Loading…</p>
      ) : error ? (
        <p className="set-status set-error">Could not load bank accounts.</p>
      ) : accounts.length === 0 ? (
        <p className="ba-empty">No bank accounts yet{canWrite ? ' — add one above.' : '.'}</p>
      ) : (
        <ul className="ba-list">
          {accounts.map((a) => (
            <li key={a.id} className={`ba-row${a.is_active ? '' : ' ba-row-inactive'}`}>
              <div className="ba-main">
                <span className="ba-label">{a.label}</span>
                {a.is_default && <span className="ba-badge ba-badge-default">Default</span>}
                {!a.is_active && <span className="ba-badge ba-badge-inactive">Inactive</span>}
              </div>
              <div className="ba-sub">
                {[
                  a.bank_name,
                  TYPE_LABEL[a.account_type] ?? a.account_type,
                  a.account_last4 ? `••••${a.account_last4}` : null,
                ].filter(Boolean).join(' · ')}
              </div>
              {canWrite && (
                <div className="ba-actions">
                  <button type="button" className="btn-text" onClick={() => openEdit(a)}>Edit</button>
                  <button
                    type="button"
                    className="btn-text ba-delete"
                    onClick={() => onDelete(a)}
                    disabled={del.isPending}
                  >
                    Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <BankAccountFormModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
